import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import type { ScheduleSummary } from "@shared/types/schedules";
import { SCHEDULES_PATH, schedulePath } from "@/hooks/use-schedules";
import { apiRequest } from "@/lib/api";

/**
 * Pause and resume (Session 10, P9): `POST /schedules/:id/pause` with `{ until }` and
 * `POST /schedules/:id/resume`, audited as `schedule.pause` and `schedule.resume`
 * (`schedules.write`). Both recompute the next run on the server, so each invalidates the list
 * and the one schedule, as every other schedule write does (`use-schedules.ts`).
 *
 * Here rather than in `use-schedules.ts`, which no Session 10 package owns; the lead may move them.
 */

function useInvalidateSchedule(): (id: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (id: string) => {
    await queryClient.invalidateQueries({ queryKey: [SCHEDULES_PATH] });
    await queryClient.invalidateQueries({ queryKey: [schedulePath(id)] });
  };
}

export function usePauseSchedule(): UseMutationResult<
  ScheduleSummary,
  unknown,
  { id: string; until: string }
> {
  const invalidate = useInvalidateSchedule();
  return useMutation({
    mutationFn: ({ id, until }) =>
      apiRequest<ScheduleSummary>(`${schedulePath(id)}/pause`, { method: "POST", body: { until } }),
    onSuccess: (_saved, { id }) => invalidate(id),
  });
}

export function useResumeSchedule(): UseMutationResult<ScheduleSummary, unknown, string> {
  const invalidate = useInvalidateSchedule();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ScheduleSummary>(`${schedulePath(id)}/resume`, { method: "POST" }),
    onSuccess: (_saved, id) => invalidate(id),
  });
}
